import { useMemo, useState } from "react";
import { hotBoard, latestNews, trendTags, type NewsItem } from "@/mocks/homeContent";

function HotRankRow({ item, idx }: { item: { rank: string; title: string; heat: string; tag: string; delta: string }; idx: number }) {
  const hot = idx < 3;
  return (
    <a href="#hot-board" className="group flex items-start gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-background-100">
      <span className={`font-heading w-7 shrink-0 text-center text-2xl leading-none ${hot ? "text-accent-600" : "text-foreground-300"}`}>
        {item.rank}
      </span>
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
          {item.title}
        </p>
        <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1 font-medium text-primary-600">
            <i className="ri-fire-line" aria-hidden="true"></i>
            {item.heat}
          </span>
          <span className="inline-flex items-center rounded-sm bg-secondary-100 px-1.5 py-0.5 text-foreground-700">{item.tag}</span>
        </div>
      </div>
      <span className={`mt-0.5 shrink-0 text-xs ${item.delta.startsWith("-") ? "text-accent-700" : "text-primary-700"}`}>
        {item.delta.startsWith("-") ? <i className="ri-arrow-down-line mr-0.5" aria-hidden="true"></i> : <i className="ri-arrow-up-line mr-0.5" aria-hidden="true"></i>}
        {item.delta}
      </span>
    </a>
  );
}

export default function FeedSection() {
  const areas = useMemo(() => Array.from(new Set(latestNews.map((n) => n.area))), []);
  const [active, setActive] = useState<string>("全部");
  const filtered = active === "全部" ? latestNews : latestNews.filter((n) => n.area === active);
  const [followed, setFollowed] = useState<number | null>(null);

  return (
    <section className="mx-auto w-full max-w-shell px-4 py-10 md:px-6 md:py-14">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        {/* 左：最新爆料短讯 */}
        <div className="lg:col-span-2">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary-600">FRESH EXPOSURES</p>
              <h2 className="mt-1 text-2xl font-black text-foreground-950 md:text-3xl">最新爆料短讯</h2>
            </div>
            <div className="flex flex-wrap items-center gap-1 rounded-md bg-background-100 p-1">
              {["全部", ...areas].map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setActive(a)}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
                    active === a ? "bg-primary-500 font-medium text-background-50" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 divide-y divide-background-200 rounded-xl border border-background-200 bg-background-50">
            {filtered.map((item: NewsItem, i: number) => (
              <a
                key={i}
                href="#feed"
                className="group block px-4 py-3.5 transition-colors hover:bg-background-100 md:px-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-[15px] font-semibold leading-snug text-foreground-950 group-hover:text-primary-700">
                    {item.title}
                  </h3>
                  <span className="mt-0.5 hidden shrink-0 whitespace-nowrap rounded-sm bg-background-100 px-2 py-0.5 text-[11px] text-foreground-500 sm:block">
                    {item.area}
                  </span>
                </div>
                <p className="mt-1 line-clamp-1 text-sm leading-relaxed text-foreground-500">{item.summary}</p>
                <div className="mt-2 flex items-center gap-3 text-xs text-foreground-400">
                  <span>{item.time}</span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-chat-3-line" aria-hidden="true"></i>
                    {item.replies}
                  </span>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-4 flex justify-center">
            <button
              type="button"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              加载更多跟帖
              <i className="ri-arrow-down-s-line" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {/* 右：热议榜 + 风向标签 + 订阅提醒 */}
        <aside id="hot-board" className="flex flex-col gap-6">
          <div className="rounded-xl border border-background-200 bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
              <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-fire-fill text-accent-600" aria-hidden="true"></i>
                实时热议榜
              </h3>
              <span className="text-xs text-foreground-400">每 10 分钟刷新</span>
            </div>
            <div className="px-2 py-2">
              {hotBoard.map((item, idx) => (
                <HotRankRow key={item.rank} item={item} idx={idx} />
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <div className="flex items-center gap-2 text-sm font-bold text-foreground-950">
              <i className="ri-flag-2-fill text-secondary-600" aria-hidden="true"></i>
              本周风向关键词
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {trendTags.map((t, i) => (
                <a
                  key={t}
                  href="#top"
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs transition-colors ${
                    i < 3 ? "bg-secondary-600 text-background-50 hover:bg-secondary-500" : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  {i < 3 && <i className="ri-arrow-up-line" aria-hidden="true"></i>}
                  {t}
                </a>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-background-900 p-5 text-background-100">
            <div className="flex items-center gap-2 text-sm font-bold text-background-50">
              <i className="ri-notification-3-line text-accent-300" aria-hidden="true"></i>
              订阅本周负面清单
            </div>
            <p className="mt-2 text-xs leading-relaxed text-background-200/90">
              每周五晚把争议事件汇总与热点归档推到你的邮箱，已订阅 {followed ?? 0} 人。
            </p>
            <div className="mt-3 flex gap-2">
              <input
                type="text"
                placeholder="常用邮箱"
                aria-label="订阅邮箱"
                className="min-w-0 flex-1 rounded-md border border-background-700 bg-background-800 px-3 py-2 text-sm text-background-50 outline-none placeholder:text-background-400"
              />
              <button
                type="button"
                onClick={() => setFollowed(Number(((followed ?? 0) + 1).toFixed(0)))}
                className="shrink-0 whitespace-nowrap rounded-md bg-accent-500 px-3 py-2 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                订阅
              </button>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}